import { createContext, useContext, useEffect, useMemo, useState, createElement } from "react";

const Ctx = createContext(null);
const read = (k, d) => {
  try {
    return JSON.parse(localStorage.getItem(k)) ?? d;
  } catch {
    return d;
  }
};
const write = (k, v) => localStorage.setItem(k, JSON.stringify(v));

export function StoreProvider({ children }) {
  const [accounts, setAccounts] = useState(() => read("xc_accounts", []));
  const [activeId, setActiveId] = useState(() => read("xc_active", null));
  const [favs, setFavs] = useState([]);
  const [recents, setRecents] = useState([]);
  const [installEvt, setInstallEvt] = useState(null);
  const [progress, setProgressMap] = useState({});
  const [liveFmt, setLiveFmtState] = useState("hls");
  const [reminders, setReminders] = useState([]);

  const active = accounts.find((a) => a.id === activeId) || accounts[0] || null;

  useEffect(() => write("xc_accounts", accounts), [accounts]);
  useEffect(() => write("xc_active", active?.id || null), [active?.id]);
  useEffect(() => {
    setFavs(active ? read(`xc_favs_${active.id}`, []) : []);
    setRecents(active ? read(`xc_recent_${active.id}`, []) : []);
    setProgressMap(active ? read(`xc_progress_${active.id}`, {}) : {});
    setLiveFmtState(active ? read(`xc_livefmt_${active.id}`, "hls") : "hls");
    setReminders(active ? read(`xc_reminders_${active.id}`, []) : []);
  }, [active?.id]);

  useEffect(() => {
    const h = (e) => {
      e.preventDefault();
      setInstallEvt(e);
    };
    window.addEventListener("beforeinstallprompt", h);
    return () => window.removeEventListener("beforeinstallprompt", h);
  }, []);

  const value = useMemo(() => {
    const saveFavs = (f) => {
      setFavs(f);
      write(`xc_favs_${active.id}`, f);
    };
    return {
      accounts,
      active,
      favs,
      recents,
      installEvt,
      clearInstall: () => setInstallEvt(null),
      addAccount: (a) => {
        const acc = { ...a, id: `acc_${Date.now()}` };
        setAccounts((x) => [...x, acc]);
        setActiveId(acc.id);
        return acc;
      },
      removeAccount: (id) => {
        setAccounts((x) => x.filter((a) => a.id !== id));
        localStorage.removeItem(`xc_favs_${id}`);
        localStorage.removeItem(`xc_recent_${id}`);
      },
      setActive: setActiveId,
      isFav: (type, id) => favs.some((f) => f.type === type && String(f.id) === String(id)),
      toggleFav: (item) => {
        const has = favs.some((f) => f.type === item.type && String(f.id) === String(item.id));
        saveFavs(has ? favs.filter((f) => !(f.type === item.type && String(f.id) === String(item.id))) : [item, ...favs]);
        return !has;
      },
      pushRecent: (item) => {
        setRecents((r) => {
          const next = [{ ...item, ts: Date.now() }, ...r.filter((x) => !(x.type === item.type && String(x.id) === String(item.id)))].slice(0, 60);
          write(`xc_recent_${active.id}`, next);
          return next;
        });
      },
      progress,
      pct: (key) => {
        const p = progress[key];
        return p?.dur ? Math.min(1, p.pos / p.dur) : 0;
      },
      resumeAt: (key) => {
        const p = progress[key];
        return p && p.pos > 10 && p.dur && p.pos / p.dur < 0.95 ? p.pos : 0;
      },
      setProgress: (entries) => {
        setProgressMap((m) => {
          const next = { ...m, ...entries };
          write(`xc_progress_${active.id}`, next);
          return next;
        });
      },
      reminders,
      hasReminder: (key) => reminders.some((r) => r.key === key),
      toggleReminder: (r) => {
        const has = reminders.some((x) => x.key === r.key);
        const next = has ? reminders.filter((x) => x.key !== r.key) : [...reminders, r].sort((a, b) => a.start - b.start);
        setReminders(next);
        write(`xc_reminders_${active.id}`, next);
        return !has;
      },
      removeReminders: (keys) => {
        setReminders((rs) => {
          const next = rs.filter((x) => !keys.includes(x.key));
          write(`xc_reminders_${active.id}`, next);
          return next;
        });
      },
      liveFmt,
      setLiveFmt: (f) => {
        setLiveFmtState(f);
        write(`xc_livefmt_${active.id}`, f);
      },
      clearRecents: () => {
        setRecents([]);
        write(`xc_recent_${active.id}`, []);
      },
    };
  }, [accounts, active, favs, recents, installEvt, progress, liveFmt, reminders]);

  return createElement(Ctx.Provider, { value }, children);
}

export const useStore = () => useContext(Ctx);
